// Server-side ident resolver — used by the route entry bar to expand
// "KSIY OED FJS BOACH KSFO" into a sequence of waypoints.

import { searchAny, SearchHit } from './search';
import type { PlanPoint, PlanPointKind } from '../stores/planStore';

export interface ResolvedIdent {
  ident: string;
  name: string;
  lat: number;
  lon: number;
  kind: PlanPointKind;
}

function hitToPlanKind(h: SearchHit): PlanPointKind {
  if (h.kind === 'airport') return 'airport';
  if (h.kind === 'fix') return 'fix';
  // navaid → vor or ndb based on badge
  if ((h.badge || '').toUpperCase().includes('NDB')) return 'ndb';
  return 'vor';
}

export async function resolveIdent(rawIdent: string): Promise<ResolvedIdent | null> {
  const norm = rawIdent.trim().toUpperCase();
  if (!norm) return null;
  // Server returns best match across all kinds with exact ident scoring 1000
  const hits = await searchAny(norm, { limit: 1 });
  const top = hits[0];
  if (!top) return null;
  // Only accept if it's an exact ident match (not a fuzzy / startsWith match)
  if (top.ident.toUpperCase() !== norm) return null;
  return {
    ident: top.ident,
    name: top.name,
    lat: top.lat,
    lon: top.lon,
    kind: hitToPlanKind(top),
  };
}

export async function resolveRoute(text: string): Promise<{
  resolved: ResolvedIdent[];
  unresolved: string[];
}> {
  const parts = text
    .split(/[\s,;]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  const resolved: ResolvedIdent[] = [];
  const unresolved: string[] = [];
  // Resolve in parallel (browser limits to ~6 concurrent)
  const results = await Promise.all(parts.map((p) => resolveIdent(p).catch(() => null)));
  for (let i = 0; i < parts.length; i++) {
    const r = results[i];
    if (r) resolved.push(r);
    else unresolved.push(parts[i]);
  }
  return { resolved, unresolved };
}

export function resolvedToPlanPoint(r: ResolvedIdent): PlanPoint {
  return { ident: r.ident, lat: r.lat, lon: r.lon, kind: r.kind };
}

export type { SearchHit };
export { searchAny };
